import {
  BaseCharacterState,
  CreatureStateType,
  CreatureType,
  HostilityType,
  MMONetSchema,
} from "mmo-common";
import {
  GameObject,
  Quat,
  TimerGameObject,
  Vector3,
  vector3Add,
} from "realms-engine";
import {
  SlashEffectColor,
  SlashEffectGameObject,
  SlashEffectType,
} from "../effects/slashEffect/SlashEffect.gameObject";
import { threeToVector3, toThreeQuaternion } from "realms-engine-browser";
import THREE from "realms-three";
import {
  BaseCharacterGameObject,
  PlayCharacterAnimationProps,
} from "../base-character/baseCharacter.gameObject";
import { MMONetworkSlave } from "@utils/types";
import { NetworkTypeEvent } from "realms-engine-network";
import {
  BASE_EMISSIVE_COLOR,
  HIT_FLASH_COLOR,
  HIT_FLASH_DURATION_MS,
} from "../config/combat";
import { CreatureUIGameObject } from "../ui/creature-ui/creatureUI.gameObject";
import { DamageNumberGameObject } from "../ui/damage-number/damageNumber.gameObject";
import { MMOCreature } from "@game/models/mmoCreature";

export class CharacterGameObject<T extends {} = {}>
  extends GameObject<T>
  implements MMONetworkSlave<CreatureType.Character>, MMOCreature
{
  protected characterGameObject: BaseCharacterGameObject;
  private shouldHideUI: boolean;
  private ui: CreatureUIGameObject;
  private physicsGameObjectId: number;
  private playingAnimation: PlayCharacterAnimationProps | null = null;

  // If true, next state tick will force reload animation
  private forceReloadAnimation = false;
  private currentStateEffects = new Set<GameObject>();

  private onHitFlashEndTimer: TimerGameObject;
  private masterState: BaseCharacterState;

  constructor(
    state: BaseCharacterState,
    options: {
      shouldHideUI?: boolean;
    } = {}
  ) {
    super();

    this.physicsGameObjectId = state.physicsGameObjectId;

    this.characterGameObject = this.addChild(
      new BaseCharacterGameObject({ character: state.character })
    );

    this.ui = this.characterGameObject.addChild(
      new CreatureUIGameObject({
        health: state.creature.health,
        maxHealth: state.creature.maxHealth,
        name: state.creature.name,
      })
    );

    this.shouldHideUI = options.shouldHideUI ?? false;
    this.ui.setVisibility(!this.shouldHideUI);

    this.onHitFlashEndTimer = this.addChild(new TimerGameObject());

    this.onHitFlashEndTimer.addListener("onFinish", () => {
      this.characterGameObject.baseCharacterMesh.setEmissive(
        new THREE.Color(BASE_EMISSIVE_COLOR)
      );
    });
    this.masterState = state;
  }

  onNetworkEvent(
    event: NetworkTypeEvent<MMONetSchema, CreatureType.Character, "onHurt">
  ): void {
    switch (event.name) {
      case "onHurt": {
        this.characterGameObject.baseCharacterMesh.setEmissive(HIT_FLASH_COLOR);
        this.onHitFlashEndTimer.start(HIT_FLASH_DURATION_MS);

        this.getScene()?.add(
          new DamageNumberGameObject({
            value: event.data.damage,
            spawnPosition: this.getPosition(),
          })
        );
        break;
      }
    }
  }

  getHostility(): HostilityType {
    return this.masterState.creature.hostility;
  }

  isDead() {
    return this.masterState.creature.state.type === CreatureStateType.Death;
  }

  getUI() {
    return this.ui;
  }

  getPhysicsGameObjectId() {
    return this.physicsGameObjectId;
  }

  /**
   * Plays the animation for the characters given state
   */
  playCharacterStateAnimation(state: BaseCharacterState) {
    this.removeAllCurrentStateEffects();
    const isDead = state.creature.state.type === CreatureStateType.Death;
    this.setShouldHideUI(isDead);
    this.playingAnimation = this.mapStateToAnimation(state);
    this.characterGameObject.playAnimation(this.playingAnimation);
    switch (this.playingAnimation?.name) {
      case "horizontalSlash": {
        this.createSlashEffect({
          type: SlashEffectType.ArcMedium,
          color: SlashEffectColor.Blue,
          duration: 300,
          delay: 400,
          width: 40,
          height: 40,
          offset: 15,
          heightOffset: 10,
        });
        break;
      }
      case "verticalSlash": {
        this.createSlashEffect({
          type: SlashEffectType.ArcMedium,
          color: SlashEffectColor.Blue,
          duration: 400,
          delay: 600,
          width: 40,
          height: 40,
          offset: 10,
          heightOffset: 10,
          rotation: new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(1, 0, 0),
            Math.PI
          ),
        });
        break;
      }
    }
    this.forceReloadAnimation = false;
  }

  onSlaveChanges(
    changes: Partial<BaseCharacterState>,
    newState: BaseCharacterState,
    oldState: BaseCharacterState
  ): void {
    this.masterState = newState;

    this.physicsGameObjectId = newState.physicsGameObjectId;

    if (changes?.creature?.position !== undefined) {
      this.setCharacterPosition(newState.creature.position);
    }
    if (changes?.creature?.quaternion !== undefined) {
      this.setCharacterQuarternion(newState.creature.quaternion);
    }
    if (changes?.creature?.name !== undefined) {
      this.ui.setCreatureName(newState.creature.name);
    }
    if (changes.creature?.state !== undefined || this.forceReloadAnimation) {
      this.playCharacterStateAnimation(newState);
    }
    if (changes?.character !== undefined) {
      this.characterGameObject.updateConfig(newState.character);
    }
    if (changes?.creature?.health !== undefined) {
      this.ui.setHealth(newState.creature.health);
    }
    if (changes?.creature?.maxHealth !== undefined) {
      this.ui.setMaxHealth(newState.creature.maxHealth);
    }
  }

  /**
   * Maps the state to the animation to play
   */
  mapStateToAnimation(state: BaseCharacterState): PlayCharacterAnimationProps {
    switch (state.creature.state.type) {
      case CreatureStateType.Idle: {
        return {
          name: "baseIdle",
        };
      }
      case CreatureStateType.Walk: {
        return {
          name: "baseWalk",
        };
      }
      case CreatureStateType.Death: {
        return {
          name: "baseDeath",
          clampWhenFinished: true,
        };
      }
      case CreatureStateType.Jump: {
        return {
          name: "baseJump",
        };
      }
      case CreatureStateType.NormalAttack: {
        return {
          name: "horizontalSlash",
          clampWhenFinished: true,
        };
      }
    }
  }

  private createSlashEffect({
    type,
    color,
    duration,
    width,
    height,
    delay,
    offset,
    heightOffset,
    reverse,
    rotation,
  }: {
    type: SlashEffectType;
    color: SlashEffectColor;
    duration: number;
    width: number;
    height: number;
    delay: number;
    offset: number;
    heightOffset: number;
    reverse?: boolean;
    rotation?: THREE.Quaternion;
  }) {
    const slash = new SlashEffectGameObject({
      type,
      color,
      duration,
      width,
      height,
      delay,
      reverse,
    });
    const quaternion = toThreeQuaternion(this.getQuaternion())
      .multiply(
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          Math.PI
        )
      )
      .multiply(
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          (3 * Math.PI) / 2
        )
      );

    if (rotation) {
      quaternion.multiply(rotation);
    }

    const faceDirection = new THREE.Vector3(1, 0, 0);
    faceDirection.applyQuaternion(quaternion);
    faceDirection.multiplyScalar(offset);

    slash.setQuaternion(quaternion);
    slash.setPosition(
      vector3Add(
        vector3Add(this.getPosition(), { x: 0, y: heightOffset, z: 0 }),
        threeToVector3(faceDirection)
      )
    );
    this.addChild(slash);
    this.currentStateEffects.add(slash);
  }

  removeAllCurrentStateEffects() {
    for (const effect of Array.from(this.currentStateEffects)) {
      effect.removeFromParent();
    }
    this.currentStateEffects.clear();
  }

  setShouldHideUI(shouldHide: boolean) {
    if (this.shouldHideUI !== shouldHide) {
      this.shouldHideUI = shouldHide;
      this.ui.setVisibility(!this.shouldHideUI);
    }
  }

  getHealth() {
    return this.ui.getHealth();
  }

  getMaxHealth() {
    return this.ui.getMaxHealth();
  }

  getPosition() {
    return this.characterGameObject.getPosition();
  }

  protected setCharacterPosition(position: Vector3) {
    this.characterGameObject.setPosition(position);
  }

  protected setCharacterQuarternion(quaternion: Quat) {
    this.characterGameObject.setQuaternion(quaternion);
  }

  getQuaternion() {
    return this.characterGameObject.getQuaternion();
  }

  setForceReloadAnimation() {
    this.forceReloadAnimation = true;
  }
}
